import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { StepIndicator } from "@/components/ui/step-indicator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  useConsents,
  CONSENT_TEXT_VERSION,
  useDebouncedValue,
  useLocationSearch,
  useLocations,
  useUserProfile,
  useUserSettings,
} from "@/hooks";
import { setAppLanguage } from "@/i18n";
import { normalizeLanguage, type AppLanguage } from "@/i18n/languages";
import { findBestLocationSuggestion } from "@/lib/location-search";
import { isOnboardingGuardDisabled, shouldForceOnboarding } from "@/lib/onboarding-flags";
import type { BodyModel, ConsentType, UpdateConsentsRequest } from "@floline/api-client";
import { ApiClientError } from "@floline/api-client";

type StepId = "language" | "name" | "paperdoll" | "location" | "consent" | "finish";

function splitName(name: string | null | undefined): { firstName: string; lastName: string } {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

/**
 * The optional consent types that the user may choose during onboarding.
 * `core_health_processing` is handled separately as a required acknowledgement.
 */
const OPTIONAL_CONSENT_TYPES: ConsentType[] = [
  "audio_transcription",
  "ai_extraction",
  "ai_insights",
];

function OnboardingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("onboarding");
  const { t: tc } = useTranslation("common");

  const { settings, isFetched: settingsFetched, updateSettings, isUpdating } = useUserSettings();
  const { profile, isLoadingProfile, updateProfile, isUpdatingProfile } = useUserProfile();
  const {
    locations,
    isLoading: locationsLoading,
    createLocation,
    isCreating,
    updateLocation,
    isUpdating: isUpdatingLocation,
    deleteLocation,
    isDeleting: isDeletingLocation,
  } = useLocations();
  const { consents, updateConsents, isUpdating: isUpdatingConsents } = useConsents();

  const totalSteps = 6;
  const steps: StepId[] = useMemo(
    () => ["language", "name", "paperdoll", "location", "consent", "finish"],
    []
  );

  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex] ?? "language";
  const hasStepHistoryRef = useRef(false);
  const ignorePopRef = useRef(false);
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;

  const [language, setLanguage] = useState<AppLanguage>(
    normalizeLanguage(i18n.resolvedLanguage ?? i18n.language)
  );
  const [bodyModel, setBodyModel] = useState<BodyModel>("male");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const [cityQuery, setCityQuery] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [temperatureEnabled, setTemperatureEnabled] = useState(false);
  const debouncedCityQuery = useDebouncedValue(cityQuery, 250);
  const {
    results: citySuggestions,
    isFetching: citySuggestionsLoading,
  } = useLocationSearch(debouncedCityQuery, { enabled: step === "location" });

  const selectedCitySuggestion = findBestLocationSuggestion(cityQuery, citySuggestions);
  const showNoMatches =
    debouncedCityQuery.trim().length >= 2 && !citySuggestionsLoading && citySuggestions.length === 0;
  const showCityHint = !citySuggestionsLoading && !showNoMatches;

  // Consent state — all start unchecked (no implied consent)
  const [coreConsent, setCoreConsent] = useState(false);
  const [audioConsent, setAudioConsent] = useState(false);
  const [extractionConsent, setExtractionConsent] = useState(false);
  const [insightsConsent, setInsightsConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // Unique id prefix for aria-describedby associations
  const idPrefix = useId();

  const isSaving =
    isUpdating || isUpdatingProfile || isCreating || isUpdatingLocation || isDeletingLocation || isUpdatingConsents;

  const resetStepErrors = useCallback(() => {
    setNameError(null);
    setLocationError(null);
    setConsentError(null);
  }, []);

  // Seed form state once settings/profile load
  useEffect(() => {
    if (!settingsFetched || !settings) return;
    setLanguage(settings.language);
    setBodyModel(settings.bodyModel);
    setTemperatureEnabled(settings.weatherOverlayEnabled);
  }, [settingsFetched, settings]);

  useEffect(() => {
    if (!profile) return;
    const baseline = splitName(`${profile.firstName} ${profile.lastName}`);
    setFirstName(baseline.firstName);
    setLastName(baseline.lastName);
  }, [profile]);

  // Seed consent state from existing consents (if user returns to onboarding)
  useEffect(() => {
    if (consents.length === 0) return;
    for (const c of consents) {
      if (c.consentType === "core_health_processing") setCoreConsent(c.given);
      if (c.consentType === "audio_transcription") setAudioConsent(c.given);
      if (c.consentType === "ai_extraction") setExtractionConsent(c.given);
      if (c.consentType === "ai_insights") setInsightsConsent(c.given);
    }
  }, [consents]);

  const canGoNext = step !== "finish";
  const canGoBack = stepIndex > 0;

  async function handleNext() {
    setNameError(null);
    setLocationError(null);
    setConsentError(null);

    if (step === "language") {
      await updateSettings({ language });
      await setAppLanguage(language);
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      return;
    }

    if (step === "name") {
      if (!firstName.trim()) {
        setNameError(t("name.required"));
        return;
      }
      if (!profile) return;

      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: profile.email,
      });
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      return;
    }

    if (step === "paperdoll") {
      await updateSettings({ bodyModel });
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      return;
    }

    if (step === "location") {
      // Persist the temperature overlay preference regardless of whether a location was added.
      await updateSettings({ weatherOverlayEnabled: temperatureEnabled });
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      return;
    }

    if (step === "consent") {
      // Core consent is required to proceed
      if (!coreConsent) {
        setConsentError(t("consent.coreHealthProcessing.required"));
        return;
      }

      try {
        // Build decisions object with full consent text and version
        const decisions: UpdateConsentsRequest["decisions"] = {
          core_health_processing: {
            given: true,
            consentTextVersion: CONSENT_TEXT_VERSION,
            consentText: t("consent.coreHealthProcessing.text"),
          },
        };

        // Optional consents — include all with their current state
        const optionalMap: Record<string, boolean> = {
          audio_transcription: audioConsent,
          ai_extraction: extractionConsent,
          ai_insights: insightsConsent,
        };

        for (const [type, given] of Object.entries(optionalMap)) {
          const keyMap: Record<string, string> = {
            audio_transcription: "audioTranscription",
            ai_extraction: "aiExtraction",
            ai_insights: "aiInsights",
          };
          const key = keyMap[type] ?? type;
          decisions[type] = {
            given,
            consentTextVersion: CONSENT_TEXT_VERSION,
            consentText: t(`consent.${key}.text`),
          };
        }

        await updateConsents({ decisions });
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      } catch {
        setConsentError(t("consent.saveError"));
      }
      return;
    }
  }

  function handleBack() {
    resetStepErrors();

    if (typeof window !== "undefined" && hasStepHistoryRef.current) {
      window.history.back();
      return;
    }

    setStepIndex((i) => Math.max(0, i - 1));
  }

  async function handleSkip() {
    resetStepErrors();

    if (step === "location") {
      // Explicitly keep temp overlay off when skipping.
      await updateSettings({ weatherOverlayEnabled: false });
    }

    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }

  async function handleFinish() {
    await updateSettings({ onboardingComplete: true });

    if (typeof window !== "undefined" && hasStepHistoryRef.current) {
      ignorePopRef.current = true;
      hasStepHistoryRef.current = false;
      setStepIndex(0);
      window.history.back();
      window.setTimeout(() => {
        ignorePopRef.current = false;
        void navigate({ to: "/", replace: true });
      }, 0);
      return;
    }

    await navigate({ to: "/", replace: true });
  }

  async function handleAddLocation() {
    const q = cityQuery.trim();
    if (!q) return;
    setLocationError(null);
    if (!selectedCitySuggestion) {
      setLocationError(t("location.selectionRequired"));
      return;
    }

    try {
      await createLocation({ query: selectedCitySuggestion.name });
      setCityQuery("");
      // If the user just added a location, enable the temperature overlay by default.
      setTemperatureEnabled(true);
      await updateSettings({ weatherOverlayEnabled: true });
    } catch (err: unknown) {
      if (err instanceof ApiClientError && err.status === 429) {
        setLocationError(t("location.rateLimitError"));
      } else {
        setLocationError(t("location.addFailed"));
      }
    }
  }

  async function handleSetDefaultLocation(id: string) {
    setLocationError(null);
    try {
      await updateLocation({ id, data: { isDefault: true } });
    } catch {
      setLocationError(t("location.setDefaultFailed"));
    }
  }

  async function handleDeleteLocation(id: string) {
    setLocationError(null);
    try {
      await deleteLocation(id);
    } catch {
      setLocationError(t("location.deleteFailed"));
    }
  }

  const guardDisabled = isOnboardingGuardDisabled();
  const force = shouldForceOnboarding();
  const isAlreadyCompleted = settingsFetched && !!settings && settings.onboardingCompletedAt !== null;

  useEffect(() => {
    if (!settingsFetched || !settings) return;
    if (guardDisabled) return;
    if (!force && settings.onboardingCompletedAt !== null) {
      void navigate({ to: "/" });
    }
  }, [settingsFetched, settings, guardDisabled, force, navigate]);

  useEffect(() => {
    if (stepIndex === 0) {
      hasStepHistoryRef.current = false;
      return;
    }
    if (typeof window === "undefined") return;
    if (hasStepHistoryRef.current) return;

    window.history.pushState({ __onboardingStep: true, step: stepIndex }, "");
    hasStepHistoryRef.current = true;
  }, [stepIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      if (ignorePopRef.current) return;
      if (!hasStepHistoryRef.current || stepIndexRef.current === 0) return;

      hasStepHistoryRef.current = false;
      resetStepErrors();
      setStepIndex((index) => Math.max(0, index - 1));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [resetStepErrors]);

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
        <StepIndicator
          current={stepIndex + 1}
          total={totalSteps}
          label={t("step", { current: stepIndex + 1, total: totalSteps })}
          className="pt-1"
        />
        {force && isAlreadyCompleted && (
          <p className="text-xs text-muted-foreground">
            Testing mode: onboarding forced via `?onboarding=1` or `VITE_ONBOARDING_FORCE=1`.
          </p>
        )}
      </div>

      {step === "language" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("language.title")}</CardTitle>
            <CardDescription>{t("language.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="onboarding-language" className="text-sm font-medium">
                {t("language.label")}
              </label>
              <Select value={language} onValueChange={(v) => setLanguage(v as AppLanguage)} disabled={isSaving}>
                <SelectTrigger id="onboarding-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t("language.english")}</SelectItem>
                  <SelectItem value="fi">{t("language.finnish")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "name" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("name.title")}</CardTitle>
            <CardDescription>{t("name.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="onboarding-first-name" className="text-sm font-medium">
                {t("name.firstName")}
              </label>
              <Input
                id="onboarding-first-name"
                autoComplete="given-name"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? `${idPrefix}-name-error` : undefined}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setNameError(null);
                }}
                disabled={isSaving || isLoadingProfile}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="onboarding-last-name" className="text-sm font-medium">
                {t("name.lastName")}
              </label>
              <Input
                id="onboarding-last-name"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSaving || isLoadingProfile}
              />
            </div>
            {nameError && (
              <p id={`${idPrefix}-name-error`} className="text-sm text-destructive" role="alert">
                {nameError}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {step === "paperdoll" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("paperdoll.title")}</CardTitle>
            <CardDescription>{t("paperdoll.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="onboarding-body-model" className="text-sm font-medium">
                {t("paperdoll.label")}
              </label>
              <Select value={bodyModel} onValueChange={(v) => setBodyModel(v as BodyModel)} disabled={isSaving}>
                <SelectTrigger id="onboarding-body-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t("paperdoll.masculine")}</SelectItem>
                  <SelectItem value="female">{t("paperdoll.feminine")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "location" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("location.title")}</CardTitle>
            <CardDescription>{t("location.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="onboarding-city" className="text-sm font-medium">
                {t("location.searchLabel")}
              </label>
              <div className="flex gap-2">
                <Input
                  id="onboarding-city"
                  list="onboarding-city-suggestions"
                  value={cityQuery}
                  onChange={(e) => {
                    setCityQuery(e.target.value);
                    setLocationError(null);
                  }}
                  placeholder={t("location.searchPlaceholder")}
                  disabled={isSaving}
                  aria-invalid={locationError ? true : undefined}
                  aria-describedby={[
                    showCityHint ? `${idPrefix}-city-hint` : null,
                    locationError ? `${idPrefix}-location-error` : null,
                  ].filter(Boolean).join(" ") || undefined}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleAddLocation();
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() => void handleAddLocation()}
                  disabled={isSaving || !selectedCitySuggestion}
                >
                  {t("location.searchButton")}
                </Button>
              </div>
              <datalist id="onboarding-city-suggestions">
                {citySuggestions.map((city) => (
                  <option key={`${city.id}:${city.latitude}:${city.longitude}`} value={city.label} />
                ))}
              </datalist>
              {citySuggestionsLoading && debouncedCityQuery.trim().length >= 2 && (
                <p className="text-xs text-muted-foreground" aria-live="polite">{t("location.searching")}</p>
              )}
              {showCityHint && (
                <p id={`${idPrefix}-city-hint`} className="text-xs text-muted-foreground">{t("location.searchHint")}</p>
              )}
              {showNoMatches && (
                <p className="text-xs text-muted-foreground" aria-live="polite">{t("location.noMatches")}</p>
              )}
              {locationError && (
                <p id={`${idPrefix}-location-error`} className="text-sm text-destructive" role="alert">
                  {locationError}
                </p>
              )}
              {!locationsLoading && locations.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("location.noLocations")}</p>
              )}
              {locations.length > 0 && (
                <ul className="space-y-2">
                  {locations.map((loc) => (
                    <li
                      key={loc.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{loc.label}</span>
                        {loc.isDefault && (
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            {t("location.defaultBadge")}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {!loc.isDefault && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleSetDefaultLocation(loc.id)}
                            disabled={isSaving}
                          >
                            {t("location.setDefault")}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDeleteLocation(loc.id)}
                          disabled={isSaving}
                        >
                          {t("location.deleteLocation")}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("location.temperatureToggle")}</p>
                <p className="text-xs text-muted-foreground">{t("location.temperatureHint")}</p>
              </div>
              <Switch
                checked={temperatureEnabled}
                onCheckedChange={(checked) => setTemperatureEnabled(checked)}
                disabled={isSaving}
              />
            </div>

            <p className="text-xs text-muted-foreground">{t("location.privacyNote")}</p>
          </CardContent>
        </Card>
      )}

      {step === "consent" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("consent.title")}</CardTitle>
            <CardDescription>{t("consent.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Core health processing — required */}
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3">
              <label htmlFor="consent-core" className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  id="consent-core"
                  checked={coreConsent}
                  onCheckedChange={(checked) => {
                    setCoreConsent(checked);
                    setConsentError(null);
                  }}
                  disabled={isSaving}
                  aria-describedby="core-consent-text"
                />
                <span className="text-sm font-medium leading-tight">
                  {t("consent.coreHealthProcessing.label")}
                </span>
              </label>
              <p id="core-consent-text" className="text-xs text-muted-foreground leading-relaxed pl-7">
                {t("consent.coreHealthProcessing.text")}
              </p>
              {consentError && (
                <p className="text-sm text-destructive pl-7" role="alert">{consentError}</p>
              )}
            </div>

            {/* Optional consents — all start unchecked */}
            {OPTIONAL_CONSENT_TYPES.map((type) => {
              const keyMap: Record<string, string> = {
                audio_transcription: "audioTranscription",
                ai_extraction: "aiExtraction",
                ai_insights: "aiInsights",
              };
              const key = keyMap[type] ?? type;
              const stateMap: Record<string, boolean> = {
                audio_transcription: audioConsent,
                ai_extraction: extractionConsent,
                ai_insights: insightsConsent,
              };
              const setterMap: Record<string, (v: boolean) => void> = {
                audio_transcription: setAudioConsent,
                ai_extraction: setExtractionConsent,
                ai_insights: setInsightsConsent,
              };

              return (
                <div key={type} className="rounded-md border p-4 space-y-3">
                  <label htmlFor={`consent-${type}`} className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      id={`consent-${type}`}
                      checked={stateMap[type] ?? false}
                      onCheckedChange={(checked) => setterMap[type]?.(checked)}
                      disabled={isSaving}
                      aria-describedby={`${type}-consent-text`}
                    />
                    <span className="text-sm font-medium leading-tight">
                      {t(`consent.${key}.label`)}
                    </span>
                  </label>
                  <p id={`${type}-consent-text`} className="text-xs text-muted-foreground leading-relaxed pl-7">
                    {t(`consent.${key}.text`)}
                  </p>
                </div>
              );
            })}

            {/* Link to full privacy policy */}
            <p className="text-xs text-muted-foreground pt-2">
              <Link to="/privacy" className="font-medium text-primary underline-offset-4 hover:underline" target="_blank">
                {tc("legal.privacyPolicy")}
              </Link>
              {" · "}
              <Link to="/terms" className="font-medium text-primary underline-offset-4 hover:underline" target="_blank">
                {tc("legal.termsOfService")}
              </Link>
            </p>
          </CardContent>
        </Card>
      )}

      {step === "finish" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("finish.title")}</CardTitle>
            <CardDescription>{t("finish.description")}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} disabled={!canGoBack || isSaving}>
          {t("actions.back")}
        </Button>

        <div className="flex items-center gap-2">
          {step === "location" && (
            <Button variant="secondary" onClick={() => void handleSkip()} disabled={isSaving}>
              {t("actions.skip")}
            </Button>
          )}

          {step === "finish" ? (
            <Button onClick={() => void handleFinish()} disabled={isSaving}>
              {t("actions.finish")}
            </Button>
          ) : (
            <Button onClick={() => void handleNext()} disabled={!canGoNext || isSaving}>
              {t("actions.next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: OnboardingPage,
});
